import * as React from 'react'
import './domain-list.scss'
import { Link } from 'react-router-dom'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { Button } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import {
    CopyOutlined,
    EyeOutlined,
    Loading3QuartersOutlined,
    QuestionCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import '@ant-design/compatible/assets/index.css'
import { Dropdown, Form, Input, Menu, Modal, Select, Skeleton, Table, Tooltip } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { DomainDto } from '@halo/dtos/domain.dto'
import { AccountService } from '@halo/services/account.service'
import { DomainService } from '@halo/services/domain.service'
import { DEFAULT_DOMAIN_INDUSTRY_ID, DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { AccountDto } from '@halo/dtos/account.dto'
import { validateFields } from '@halo/_utils/antd'
import { simpleFormErrorNotification } from '@halo/_utils/notifications'
import moment from 'moment-timezone'
import { prepareWhitelistDomains } from '@halo/_utils/domain'
import { ApiEnumService } from '@halo/services/api-enum.service'
import clone from 'clone-deep'
import { Flags } from '@pushly/aquarium-utils/lib/enums/flags'
import GenerateTestDomainModal from '@halo/components/generate-test-domain-modal/generate-test-domain-modal'
import { FormInstance, RuleObject } from 'antd/lib/form'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { asCaslSubject, CurrentUserCan, ICaslSubject } from '@halo/stores/ability.state'
import DataTableView from '@halo/components/data-table-view/data-table-view'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'
import { Select as AqeSelect } from '@halo/components/select/select'
import { StatusType } from '@pushly/aqe/lib/enums'
import deepEqual from 'react-fast-compare'
import { Industry } from '@pushly/models/lib/structs/industry'

interface INewDomainPrompt {
    accountId?: number
    onOk: (domainDto: Partial<DomainDto>) => any
    onCancel: () => any
    visible: boolean
}

interface INewDomainPromptState {
    accounts: AccountDto[]
    currencies: any[]
    industries: Industry[]
    isLoading: boolean
}

export class NewDomainPrompt extends React.Component<INewDomainPrompt, INewDomainPromptState> {
    public state: INewDomainPromptState = {
        accounts: [],
        currencies: [],
        industries: [],
        isLoading: false,
    }

    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected domainService: DomainService
    protected apiEnumService: ApiEnumService

    protected displayNameRef: any
    protected formRef = React.createRef<FormInstance>()

    public constructor(props: INewDomainPrompt) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.domainService = Container.get(DomainService)
        this.apiEnumService = Container.get(ApiEnumService)

        this.fetchAllOrgs()
        this.fetchAllCurrencies()
    }

    public async componentDidMount() {
        this.autofocusDisplayName()
        await this.fetchAllIndustries()
    }

    public componentWillUnmount() {
        // Destroy any open modals to prevent memory leaks
        Modal.destroyAll()
    }

    public render() {
        const { accounts, currencies, industries } = this.state
        const loading = accounts.length === 0 || currencies.length === 0

        const timezones = moment.tz.names()
        const suggestedTimezone = moment.tz.guess()

        let parentTz
        if (this.props.accountId) {
            parentTz = accounts.find((a) => a.id.toString() === this.props.accountId.toString())?.timezone
        }

        return (
            <Modal
                className={classnames('new-domain-prompt')}
                title="New Domain"
                visible={this.props.visible}
                onOk={this.handleOk}
                onCancel={this.handleCancel}
                confirmLoading={this.state.isLoading}
            >
                <Skeleton loading={loading} active={true} title={false}>
                    {!loading && (
                        <Form
                            ref={this.formRef}
                            {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                            className="hide-inline-errors"
                            requiredMark={false}
                        >
                            <Form.Item
                                name="organization"
                                label="Organization"
                                initialValue={!this.props.accountId ? undefined : this.props.accountId.toString()}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Organization is required.',
                                    },
                                ]}
                            >
                                <Select size="small" disabled={!!this.props.accountId}>
                                    {accounts.map((account) => (
                                        <Select.Option key={account.id} value={account.id.toString()}>
                                            {account.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="displayName"
                                label="Display Name"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Display name is required.',
                                    },
                                ]}
                            >
                                <Input ref={(el) => (this.displayNameRef = el)} size="small" autoComplete="off" />
                            </Form.Item>

                            <Form.Item
                                name="name"
                                label="Domain"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Domain is required.',
                                    },
                                ]}
                            >
                                <Input size="small" autoComplete="off" />
                            </Form.Item>

                            <Form.Item
                                name="timezone"
                                label="Time Zone"
                                initialValue={parentTz ?? suggestedTimezone}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Time Zone is required.',
                                    },
                                ]}
                            >
                                <Select size="small" showSearch={true} placeholder="Select a timezone">
                                    {timezones.map((tz: string) => (
                                        <Select.Option key={tz} value={tz}>
                                            {tz}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="currencyCode"
                                label="Currency"
                                initialValue="USD"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Currency is required.',
                                    },
                                ]}
                            >
                                <Select
                                    size="small"
                                    showSearch={true}
                                    placeholder="Select a currency"
                                    optionFilterProp="title"
                                >
                                    {currencies.map((curr: any) => (
                                        <Select.Option
                                            key={curr.code}
                                            value={curr.code}
                                            title={`${curr.code} ${curr.name}`}
                                        >
                                            {curr.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="industry"
                                label="Industry"
                                initialValue={DEFAULT_DOMAIN_INDUSTRY_ID}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Industry is required.',
                                    },
                                ]}
                            >
                                <Select
                                    size="small"
                                    showSearch={true}
                                    placeholder="Select an Industry"
                                    optionFilterProp="title"
                                >
                                    {industries.map((industry: any) => (
                                        <Select.Option key={industry.id} value={industry.id} title={`${industry.name}`}>
                                            {industry.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="whitelistDomains" label="SDK Whitelist" initialValue={[]}>
                                <Select
                                    size="small"
                                    mode="tags"
                                    className="domains-allowed stacked-tags"
                                    dropdownClassName="disabled whitelist-domains-dropdown"
                                    placeholder="https://www.pushly.com, https://subdomain.pushly.com"
                                    getPopupContainer={this.appService.getAppContainer}
                                    tokenSeparators={[',', ';', ' ', '\n']}
                                    notFoundContent=""
                                />
                            </Form.Item>
                        </Form>
                    )}
                </Skeleton>
            </Modal>
        )
    }

    protected handleOk = async () => {
        const form = this.formRef.current
        this.setState({
            isLoading: true,
        })
        try {
            await validateFields(form)
            const formValues = form.getFieldsValue()

            const protocolCheck = /^http[s]?:\/\//i
            const fieldsToSanitize: string[] = ['name']
            fieldsToSanitize.forEach((field) => {
                if (formValues[field] && protocolCheck.test(formValues[field])) {
                    const clean = formValues[field].replace(protocolCheck, '')
                    formValues[field] = clean
                    form.setFieldsValue({ [field]: clean })
                }
            })

            formValues.whitelistDomains = prepareWhitelistDomains(formValues.whitelistDomains)

            const dto: Partial<DomainDto> = {}
            dto.account_id = formValues.organization
            dto.display_name = formValues.displayName
            dto.name = formValues.name

            dto.timezone = formValues.timezone
            dto.currency_code = formValues.currencyCode ?? 'USD'
            dto.whitelist_domains = formValues.whitelistDomains
            dto.industry_id = formValues.industry ?? DEFAULT_DOMAIN_INDUSTRY_ID

            this.props.onOk(dto)
        } catch (err) {
            this.setState({
                isLoading: false,
            })
            console.log(err)
            simpleFormErrorNotification(err)
        }
    }

    protected handleCancel = () => {
        this.props.onCancel()
    }

    protected autofocusDisplayName(): void {
        if (this.props.visible) {
            if (!this.displayNameRef) {
                setTimeout(() => this.autofocusDisplayName(), 50)
                return
            }

            if (this.props.accountId) {
                this.displayNameRef.focus()
            }
        }
    }

    protected async fetchAllOrgs(): Promise<void> {
        if (this.props.visible) {
            const { ok, data: accounts } = await this.accountService.fetchAll({
                query: { pagination: 0 },
                showLoadingScreen: false,
            })

            if (ok) {
                this.setState({ accounts })
            }
        }
    }

    protected async fetchAllCurrencies(): Promise<void> {
        if (this.props.visible) {
            const { ok, data: currencies } = await this.apiEnumService.fetchAllCurrencies({
                query: {
                    pagination: 0,
                },
                showLoadingScreen: false,
            })

            if (ok) {
                this.setState({ currencies })
            }
        }
    }

    protected async fetchAllIndustries(): Promise<void> {
        if (this.props.visible) {
            const { ok, data: industries } = await this.apiEnumService.fetchAllIndustries({
                query: { pagination: 0 },
                showLoadingScreen: false,
            })

            if (ok) {
                this.setState({ industries })
            }
        }
    }
}

interface IDomainList {
    options: {
        account?: AccountDto
    }
}

interface IDomainListState {
    loading: boolean
    domains: DomainDto[]
    domainsMeta: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
    showNewDomainPrompt: boolean
    showNewTestDomainPrompt: boolean
    generateDomain: any
    statusFilter: string[]
}

export class DomainList extends React.Component<IDomainList, IDomainListState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected domainService: DomainService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.domainService = Container.get(DomainService)

        this.state = {
            loading: true,
            domains: [],
            domainsMeta: {},
            page: 1,
            pageSize: 15,
            showNewDomainPrompt: false,
            showNewTestDomainPrompt: false,
            generateDomain: {},
            statusFilter: [StatusType[StatusType.ACTIVE]],
        }
    }

    public componentDidMount(): void {
        this.fetchDomains()
    }

    public componentWillUnmount(): void {
        // Destroy any open modals to prevent memory leaks
        Modal.destroyAll()
    }

    public render() {
        let caslCreateSubject: SubjectEntity | ICaslSubject<SubjectEntity.DOMAIN> = SubjectEntity.DOMAIN
        if (this.props.options.account?.id) {
            caslCreateSubject = asCaslSubject(SubjectEntity.DOMAIN, {
                accountId: this.props.options.account.id,
            })
        }

        return (
            <div className={classnames('domain-list')}>
                <DataTableView
                    searchPlaceholder="Search ..."
                    onSearchChange={this.searchDomains}
                    actions={
                        <CurrentUserCan do={AbilityAction.CREATE} on={caslCreateSubject}>
                            <Button type="primary" size="small" onClick={this.showNewDomainPrompt}>
                                New Domain
                            </Button>
                        </CurrentUserCan>
                    }
                    filtersOverride={
                        <React.Fragment>
                            <div className="data-table-filter">
                                <AqeSelect
                                    className="status-filter data-table-filter-select"
                                    dropdownClassName="data-table-filter-select-overlay"
                                    mode="multiple"
                                    size="small"
                                    prefix="Status"
                                    disableSearch={true}
                                    disableSelectAll={true}
                                    defaultValue={this.state.statusFilter}
                                    options={[
                                        { value: StatusType[StatusType.ACTIVE], label: 'Active' },
                                        { value: StatusType[StatusType.DISABLED], label: 'Disabled' },
                                    ]}
                                    onClose={this.filterDomainStatuses}
                                    maxDisplayCount={2}
                                    maxDisplayFormatter={(options: any[]) => {
                                        return (
                                            <Tooltip title={options.map((o) => o.label).join(', ')}>
                                                {options.length} Selected
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </div>
                        </React.Fragment>
                    }
                >
                    <Table
                        size="small"
                        bordered={false}
                        dataSource={this.state.domains}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={this.state.paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchDomains())
                                }}
                                onNext={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchDomains())
                                }}
                            />
                        )}
                        rowKey={(record) => record.id.toString()}
                        loading={
                            this.state.loading && {
                                indicator: <Loading3QuartersOutlined spin={true} />,
                            }
                        }
                        locale={{
                            emptyText: <AntdTableEmptyPlaceholder text="No Domains" icon="global" />,
                        }}
                    >
                        <Table.Column className="id" title="ID" dataIndex="id" />
                        <Table.Column className="domain" title="Domain" render={this.renderDomainColumn} />
                        <Table.Column className="status" title="Status" render={this.renderStatus} />
                        <Table.Column className="actions" title="Actions" render={this.renderRowActions} />
                    </Table>
                </DataTableView>

                <NewDomainPrompt
                    key={`${this.state.showNewDomainPrompt ? 'v' : 'h'}-new-domain-for-${
                        this.props.options.account?.id ?? '0'
                    }`}
                    accountId={this.props.options.account?.id}
                    visible={this.state.showNewDomainPrompt}
                    onOk={this.handleDomainCreation}
                    onCancel={this.hideNewDomainPrompt}
                />

                <GenerateTestDomainModal
                    domain={this.state.generateDomain}
                    visible={this.state.showNewTestDomainPrompt}
                    onCancel={this.hideNewTestDomainPrompt}
                    onOk={this.handleTestDomainCreation}
                />
            </div>
        )
    }

    protected get paginationConfig(): any {
        return {
            pageSize: this.state.pageSize,
            total: this.state.domainsMeta.total,
            current: this.state.page,
            showSizeChanger: false,
            size: 'small',
            simple: true,
            showTotal: (total: number) => (
                <span>
                    ${total} {total === 1 ? 'Domain' : 'Domains'}
                </span>
            ),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }

                return originalElement
            },
            onChange: async (page: number, pageSize: number) => {
                await this.setState({ page })
                await this.fetchDomains()
            },
        }
    }

    protected renderDomainColumn = (_: string, domain: DomainDto): React.ReactNode => {
        return (
            <TableRowEntityDisplay
                title={<Link to={this.buildDomainUrl(domain)}>{domain.display_name}</Link>}
                status={!this.props.options.account?.id && domain.account_name}
                badges={domain.name}
            />
        )
    }

    protected renderStatus = (_: string, domain: DomainDto): React.ReactNode => {
        return <StatusBadge type="domain" status={domain.status} expanded={true} />
    }

    protected renderRowActions = (_: any, domain: DomainDto): React.ReactNode => {
        const actions: any[] = []

        const domainFlags = domain.flags ?? []
        const demoIdRgx = /DEMO_ID_(?<demo_id>[\d]+)/i
        const flagMatch = demoIdRgx.exec(domainFlags.join(','))
        const demoDomainId = flagMatch?.groups?.demo_id
        const isDemoDomain = domainFlags.includes(Flags.domain.DEMO.key) && !!demoDomainId
        const isProdDomain = domain.prod_domain_id === null || domain.prod_domain_id === undefined
        const canUpdateDomain = this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(SubjectEntity.DOMAIN, {
                ...domain,
                statusId: domain.status_id,
            }),
        )

        if (canUpdateDomain) {
            if (isProdDomain && !isDemoDomain) {
                actions.push({
                    key: 'generate-test-domain',
                    text: 'Generate Test Domain',
                    icon: <CopyOutlined />,
                })
            }

            if (domain.status === 'ACTIVE') {
                actions.push({
                    key: 'disable',
                    text: 'Disable',
                    icon: <EyeOutlined />,
                })
            }

            if (domain.status === 'DISABLED') {
                actions.push({
                    key: 'enable',
                    text: 'Enable',
                    icon: <EyeOutlined />,
                })
            }
        }

        return !actions.length ? (
            <Button
                size="small"
                href={this.buildDomainUrl(domain)}
                onClick={() => this.handlePrimaryActionClick(domain)}
            >
                View
            </Button>
        ) : (
            <Dropdown.Button
                size="small"
                className="list-link-button"
                href={this.buildDomainUrl(domain)}
                onClick={(ev) => this.handlePrimaryActionClick(domain, ev)}
                overlay={
                    <Menu onClick={({ key, domEvent }) => this.handleActionClick(key, domain, domEvent)}>
                        {actions.map((item) => (
                            <Menu.Item key={item.key}>
                                {item.icon}
                                <span>{item.text}</span>
                            </Menu.Item>
                        ))}
                    </Menu>
                }
            >
                View
            </Dropdown.Button>
        )
    }

    protected buildDomainUrl(domain: DomainDto): string {
        let url = `/domains/${domain.id}`
        if (this.props.options.account?.id) {
            url = `/organizations/${this.props.options.account?.id}${url}`
        }

        return url
    }

    protected async jumpToDomain(domain: DomainDto): Promise<void> {
        interface IDomainRouteState {
            account: AccountDto
            domain?: DomainDto
        }
        const routeState: IDomainRouteState = {
            account: this.props.options.account,
        }

        const fullDomainReq = await this.domainService.fetchById(domain.id)
        if (fullDomainReq.ok) {
            routeState.domain = fullDomainReq.data
        }

        this.appService.route(this.buildDomainUrl(domain), routeState)
    }

    protected showNewDomainPrompt = async (): Promise<void> => {
        return this.setState({ showNewDomainPrompt: true })
    }

    protected hideNewDomainPrompt = async (): Promise<void> => {
        return this.setState({ showNewDomainPrompt: false })
    }

    protected handleDomainCreation = async (domainDto: Partial<DomainDto>): Promise<void> => {
        const { ok, data: domain } = await this.domainService.create(domainDto, {
            showLoadingScreen: true,
        })

        if (ok) {
            await this.appState.abilityStore.refresh()
            this.jumpToDomain(domain)
        }
    }

    protected handleTestDomainCreation = (domain: DomainDto): void => {
        this.jumpToDomain(domain)
    }

    protected showNewTestDomainPrompt = (domain: DomainDto) => {
        this.setState({
            showNewTestDomainPrompt: true,
            generateDomain: clone(domain),
        })
    }

    protected hideNewTestDomainPrompt = () => {
        return this.setState({
            showNewTestDomainPrompt: false,
            generateDomain: {},
        })
    }

    protected handlePrimaryActionClick(domain: DomainDto, event?: React.MouseEvent<HTMLButtonElement>): void {
        if (event?.metaKey) {
            window.open(this.buildDomainUrl(domain))
        } else {
            event.preventDefault()
            this.jumpToDomain(domain)
        }
    }

    protected handleActionClick = (
        key: string | number,
        domain: DomainDto,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'generate-test-domain') {
            this.showNewTestDomainPrompt(domain)
        } else if (key === 'disable') {
            const formRef = React.createRef<FormInstance>()

            Modal.confirm({
                title: 'Disable Domain',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current

                    if (!!form) {
                        await form.validateFields()
                        return this.handleDisable(domain)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to disable this domain?</p>

                        <p>
                            Please type the Domain name, <b>{domain.name}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback('Account name is required.')
                                        } else if (value !== domain.name) {
                                            callback('Account name does not match.')
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'enable') {
            Modal.confirm({
                title: 'Enable Domain',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => this.handleEnable(domain),
                content: <p>Are you sure you want to enable this domain?</p>,
            } as any)
        }
    }

    protected handleDisable = async (domain: DomainDto) => {
        await this.domainService.patch(domain.id, {
            status: 'DISABLED',
        })

        return this.fetchDomains()
    }

    protected handleEnable = async (domain: DomainDto) => {
        await this.domainService.patch(domain.id, {
            status: 'ACTIVE',
        })

        return this.fetchDomains()
    }

    protected filterDomainStatuses = async (statusFilter: string[]) => {
        if (!deepEqual(statusFilter, this.state.statusFilter)) {
            await this.setState({ statusFilter })

            return this.fetchDomains()
        }
    }

    protected searchDomains = async (value: string): Promise<void> => {
        let query = value.trim()

        if (value.length < 2) {
            query = undefined
        }

        await this.setState({ query })

        return this.fetchDomains()
    }

    protected async fetchDomains(): Promise<void> {
        let update: any = { loading: true }
        await this.setState(update)

        update.loading = false
        update.domains = []

        const domainFields = [
            'id',
            'prod_domain_id',
            'name',
            'display_name',
            'account_name',
            'account_id',
            'status',
            'status_id',
            'flags',
        ]

        let response: IApiResponse<DomainDto[]> = { ok: false, data: [] }
        const options = {
            showLoadingScreen: false,
            query: {
                page: this.state.page,
                limit: this.state.pageSize,
                search: this.state.query,
                status: this.state.statusFilter.join(','),
                fields: domainFields.join(','),
            },
        }

        if (this.props.options.account?.id) {
            response = await this.accountService.fetchDomains(this.props.options.account?.id, options)
        } else {
            response = await this.domainService.fetchAll(options)
        }

        if (response && response.ok) {
            update.domains = response.data
            update.paginationLinks = response.meta.links ?? this.state.paginationLinks
            update.domainsMeta = response.meta
        }

        return this.setState(update)
    }
}
